import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import axios from 'axios';
import { AtSign, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable note/textarea input with @mention typeahead.
 * Tag other personnel in any note; tagged users get emailed by the backend.
 *
 * Props:
 *  - value, onChange(text)
 *  - mentions (array of user ids), onMentionsChange(ids)
 *  - placeholder, rows, testId
 */
export const MentionTextarea = ({
  value,
  onChange,
  mentions = [],
  onMentionsChange,
  placeholder = 'Add a note… type @ to tag someone',
  rows = 4,
  testId = 'mention',
  onSubmitShortcut,
}) => {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState(null); // active "@..." text, or null
  const [triggerStart, setTriggerStart] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const ref = useRef(null);

  // Auto-grow the textarea to fit its content (grows while typing; on edit it
  // opens already sized to show the whole message). Caps at a max height, then
  // scrolls.
  const MAX_HEIGHT = 480;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const h = Math.min(el.scrollHeight, MAX_HEIGHT);
    el.style.height = `${h}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }, [value]);

  useEffect(() => {
    axios
      .get(`${API}/comms/board/taggable-users`)
      .then((res) => setUsers(res.data || []))
      .catch(() => setUsers([]));
  }, []);

  const matches =
    query !== null
      ? users.filter((u) => (u.name || '').toLowerCase().includes(query.toLowerCase())).slice(0, 8)
      : [];

  const userById = (id) => users.find((u) => u.id === id);

  const handleChange = (e) => {
    const text = e.target.value;
    onChange(text);
    const pos = e.target.selectionStart;
    const before = text.slice(0, pos);
    const m = before.match(/@([^\s@]*)$/);
    if (m) {
      setQuery(m[1]);
      setTriggerStart(pos - m[1].length - 1);
      setActiveIdx(0);
    } else {
      setQuery(null);
    }
  };

  const selectMention = (u) => {
    const after = value.slice(triggerStart + (query?.length || 0) + 1);
    const before = value.slice(0, triggerStart);
    onChange(`${before}@${u.name} ${after}`);
    if (!mentions.includes(u.id)) onMentionsChange([...mentions, u.id]);
    setQuery(null);
    setTimeout(() => ref.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (query !== null && matches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % matches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectMention(matches[Math.min(activeIdx, matches.length - 1)]);
        return;
      }
      if (e.key === 'Escape') {
        setQuery(null);
        return;
      }
    }
    if (onSubmitShortcut && (e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      onSubmitShortcut();
    }
  };

  const removeMention = (id) => onMentionsChange(mentions.filter((x) => x !== id));

  return (
    <div className="relative flex-1">
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        style={{ minHeight: `${rows * 1.6}rem` }}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none overflow-hidden"
        data-testid={`${testId}-input`}
      />

      {query !== null && matches.length > 0 && (
        <div
          className="absolute left-1 right-1 z-30 mt-1 max-h-52 overflow-y-auto rounded-lg border bg-white shadow-lg"
          data-testid={`${testId}-typeahead`}
        >
          {matches.map((u, idx) => (
            <button
              type="button"
              key={u.id}
              onMouseDown={(e) => {
                e.preventDefault();
                selectMention(u);
              }}
              className={`w-full flex items-center gap-2 text-left text-sm px-3 py-1.5 ${
                idx === activeIdx ? 'bg-blue-50' : 'hover:bg-muted/50'
              }`}
              data-testid={`${testId}-option-${u.id}`}
            >
              <AtSign size={13} className="text-blue-600" />
              {u.name}
              {mentions.includes(u.id) && <Check size={13} className="text-green-600 ml-auto" />}
            </button>
          ))}
        </div>
      )}

      {mentions.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid={`${testId}-chips`}>
          {mentions.map((id) => {
            const u = userById(id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 rounded-full px-2 py-0.5"
                data-testid={`${testId}-chip-${id}`}
              >
                <AtSign size={11} />
                {u ? u.name : 'user'}
                <button type="button" onClick={() => removeMention(id)} className="hover:text-red-600">
                  <X size={11} />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};
